'use client';
import { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { getCookie, setCookie } from 'cookies-next';

// components
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import Image from 'next/image';

// types
import { THeaderSupportedElements } from '@/components/atoms/at-header/at-header.types';
import {
  IResponsiveImageProps,
  IGalleryImageProps,
} from './or-image-gallery.types';

// utils + hooks
import useBreakpoint from '@/hooks/useBreakpoint';
import { buildImageUrl } from '@/components/atoms/at-media/at-media';
import { BLUR_PLACEHOLDERS } from '@/components/atoms/at-media/utils/blur-placeholder-generator';

import { MlLightbox } from '@/components/molecules';
import { mapGalleryImagesToSlides } from '@/components/molecules/ml-lightbox/utils';
import { OrImageGalleryFilters } from './image-gallery-filters';
import { AtClickable, AtHeader, AtTag, AtText } from '@/components/atoms';
import {
  ETextSize,
  ETextVariants,
} from '@/components/atoms/at-text/at-text.types';
import { EClickableTypes } from '@/components/atoms/at-clickable/at-clickable.types';
import {
  EClickableVariants,
  EClickableSize,
} from '../or-video-hero/or-video-hero.types';
import { SlideImage } from 'yet-another-react-lightbox';
import { ETagVariants } from '@/components/atoms/at-tag/at-tag.types';

const MAX_AGE = 60 * 60 * 24 * 365;

// Deterministic pseudo-random number generator for aspect ratio variance
function pseudoRandom(index: number) {
  return Math.sin(index * 9999) * 0.5 + 0.5; // value between 0 and 1
}

// Slightly adjust aspect ratio for more organic masonry layout
function getDeterministicAspectRatio(
  originalWidth: number,
  originalHeight: number,
  index: number,
  variancePercent: number = 20,
): number {
  const originalAspect = originalHeight / originalWidth;
  const variance = originalAspect * (variancePercent / 100);
  const factor = pseudoRandom(index) * 2 - 1; // -1 to 1
  return originalAspect + factor * variance;
}

// ------------------------------------
// ResponsiveImage
// ------------------------------------
// A wrapper around next/image that:
// - Handles blur-up placeholder logic
// - Falls back gracefully if an image fails
// - Allows optional "deterministic aspect ratio variance" for a more organic masonry look
// - Provides priority loading for first N images
//
// Notes:
// - If width/height missing and fill=false → falls back to square placeholder.
// - "adjustedAspectRatio" will slightly vary aspect ratio based on index for visual balance.
// - Error state renders a neutral placeholder with "Image unavailable".
export function ResponsiveImage({
  src,
  alt,
  width,
  height,
  index = 0,
  className,
  fill = false,
  priorityOverride,
  adjustedAspectRatio = false,
}: IResponsiveImageProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Generate appropriate blur placeholder
  const blurDataURL = useMemo(() => {
    return BLUR_PLACEHOLDERS.light;
  }, []);

  const imageUrl = buildImageUrl(src, {
    width: 1024,
    quality: 65,
    format: 'webp',
  });

  // Priority logic: explicit override, else index < 6 for eager loading
  const priority =
    priorityOverride !== undefined ? priorityOverride : index < 6;

  const handleLoad = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
  }, []);

  const handleError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
    console.error('Failed to load image:', imageUrl);
  }, [imageUrl]);

  // Aspect ratio calculation (with optional deterministic variance)
  const aspectRatio = useMemo(() => {
    if (!width || !height) return 1;
    if (adjustedAspectRatio) {
      return getDeterministicAspectRatio(width, height, index, 20); // ±20%
    }
    return height / width;
  }, [width, height, adjustedAspectRatio, index]);

  // If no dimensions and not using fill → render placeholder box
  if (!fill && (!width || !height)) {
    return <div className="aspect-square w-full rounded-30 bg-gray-200" />;
  }

  return (
    <figure
      className="relative w-full overflow-hidden rounded-30"
      style={!fill ? { aspectRatio: `${1 / aspectRatio}` } : undefined}
      aria-busy={isLoading}
      aria-label={alt}
    >
      {!hasError ? (
        <Image
          src={imageUrl}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          {...(fill ? { fill: true } : { width, height })}
          className={classNames('object-cover', className, {
            'h-full w-full': fill,
          })}
          quality={60}
          priority={priority}
          placeholder="blur"
          blurDataURL={blurDataURL}
          sizes={
            fill
              ? '(max-width: 2048px) 75vw, 50vw'
              : '(max-width: 2048px) 50vw, 30vw'
          }
        />
      ) : (
        <div
          className={classNames(
            className,
            'bg-gray-100object-cover flex items-center justify-center',
          )}
          role="img"
          aria-label="Image failed to load"
        >
          <span className="text-sm text-gray-400">Image unavailable</span>
        </div>
      )}
    </figure>
  );
}

export interface IOrImageGalleryProps {
  label?: string;
  label_element?: THeaderSupportedElements;
}

// ------------------------------------
// OrImageGallery
// ------------------------------------
// Main image gallery component with:
// - Masonry layout (responsive, dynamic gutters based on breakpoints)
// - Infinite scroll / lazy render (loads more on scroll, stops at footer)
// - Client-side filter system (plan series, room, exterior, location)
// - Cookie persistence (filters survive reload)
// - Lightbox integration (slides from filtered images)
// - "Reset Filters" fallback if no matches
//
// Key Behaviors:
// - Uses cookies ("imageGalleryFilters") to remember last used filters (1 year expiry).
// - Sorting priority:
//     1. "priority" field
//     2. Published date
//     3. Updated date
// - Infinite scroll threshold adjusts by screen size (smaller screens load more aggressively).
// - When clicking an image, opens lightbox at that index.
// - Lightbox footer allows quick filtering by related tags (tags act as "shortcuts" into filter state).
//
// Dev Notes:
// - "visibleCount" controls how many items render (performance safeguard).
// - "bp" (breakpoint) controls Masonry gutter spacing.
// - All filter logic is done client-side — be mindful of very large image sets.
// - `mapGalleryImagesToSlides` transforms raw gallery images into the format used by MlLightbox.
// - `ResponsiveImage` handles most of the tricky image presentation logic.

export const OrImageGallery = (props: IOrImageGalleryProps): JSX.Element => {
  const gridRef = useRef<HTMLDivElement | null>(null);
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Mobile Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);

  // fetching images from gallery api route
  const [images, setImages] = useState<IGalleryImageProps[]>([]);
  const [filteredImages, setFilteredImages] = useState<IGalleryImageProps[]>(
    [],
  );
  const [totalImgCount, setTotalImgCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);

  // Selected filter state
  const [selectedFilters, setSelectedFilters] = useState<{
    planSeries: string[];
    room: string[];
    exterior: string[];
    location: string[];
  }>({
    planSeries: [],
    room: [],
    exterior: [],
    location: [],
  });

  // fetch imgs on load
  useEffect(() => {
    const fetchImgsOnLoad = async () => {
      if (isLoading) return;
      setIsLoading(true);
      try {
        const res = await fetch(`/api/gallery/images`);

        if (res.ok) {
          const data = await res.json();
          setImages(data.images);
          setFilteredImages(data.images);
          setTotalImgCount(data.totalImgCount);
        } else {
          console.error('Failed to fetch images', res.statusText);
        }
      } catch (err) {
        console.error('Failed to fetch images:', err);
      } finally {
        setIsLoading(false);
        setIsPageLoading(false);
      }
    };
    fetchImgsOnLoad();
    //eslint-disable-next-line
  }, []);

  const bp = useBreakpoint();

  // Gutter spacing changes with breakpoint
  const gutterVal = useMemo(() => {
    if (bp === 'xl' || bp === '2xl' || bp === '3xl') {
      return '2.5rem';
    }

    if (bp === 'md' || bp === 'lg') {
      return '1.5rem';
    }

    if (bp === 'xs' || bp === 'sm') {
      return '1rem';
    }
  }, [bp]);

  // Open lightbox at clicked index
  const handleGalleryImageClick = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  // hide genesys chat iframe when either mobile nav or gallery lightbox are open
  useEffect(() => {
    if (lightboxOpen || drawerOpen) {
      const chatEl = document.getElementById('genesys-messenger');
      const iframe = chatEl?.querySelector(
        'div > iframe',
      ) as HTMLIFrameElement | null;
      if (iframe) iframe.style.zIndex = '99';
    }
  }, [drawerOpen, lightboxOpen]);

  // Lightbox slide data
  const lightboxSlides = useMemo(() => {
    return mapGalleryImagesToSlides(filteredImages);
  }, [filteredImages]);

  const hasNoResults = useMemo(() => {
    return filteredImages.length === 0;
  }, [filteredImages.length]);

  // Handler for clicking a tag => apply filter and close lightbox
  const handleFilterByTag = (key: string, value: string) => {
    const filterKeyMap: Record<string, keyof typeof selectedFilters> = {
      PlanId: 'planSeries',
      RoomId: 'room',
      ExteriorStyleId: 'exterior',
      StudioId: 'location',
    };

    const filterKey = filterKeyMap[key];
    if (!filterKey) return;

    setSelectedFilters({
      planSeries: filterKey === 'planSeries' ? [String(value)] : [],
      room: filterKey === 'room' ? [String(value)] : [],
      exterior: filterKey === 'exterior' ? [String(value)] : [],
      location: filterKey === 'location' ? [String(value)] : [],
    });

    setLightboxOpen(false);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  interface IFilters {
    planSeries: string[];
    room: string[];
    exterior: string[];
    location: string[];
  }

  const handleFilterChange = useCallback(
    async (filterKey: string, filterVals: string | string[]) => {
      setSelectedFilters((prev: IFilters) => ({
        ...prev,
        [filterKey]: filterVals,
      }));
    },
    [],
  );

  // filter imgs
  useEffect(() => {
    const tagMap = {
      planSeries: 'PlanId',
      room: 'RoomId',
      exterior: 'ExteriorStyleId',
      location: 'StudioId',
    } as const;

    const newFilteredImages = images.filter(({ tags }) => {
      return (
        Object.entries(selectedFilters) as [keyof IFilters, string[]][]
      ).every(([filterKey, filterVals]) => {
        if (!filterVals.length) return true;

        const tagKey = tagMap[filterKey];
        const tagValue = tags[tagKey];

        return filterVals.includes(String(tagValue));
      });
    });

    setFilteredImages(newFilteredImages);
  }, [selectedFilters, images]);

  // Load filters from cookie on mount
  useEffect(() => {
    const cookieFilters = getCookie('imageGalleryFilters');
    if (cookieFilters) {
      try {
        const parsed = JSON.parse(cookieFilters as string);
        setSelectedFilters(parsed);
      } catch {
        return;
      }
    }
  }, []);

  // Persist filters into cookie whenever they change
  useEffect(() => {
    setCookie('imageGalleryFilters', JSON.stringify(selectedFilters), {
      maxAge: MAX_AGE,
    });
  }, [selectedFilters]);

  return (
    <>
      <section className="pb-md container mx-auto max-w-[110.625rem]">
        {/* Lightbox */}
        <MlLightbox
          slides={lightboxSlides}
          isOpen={lightboxOpen}
          index={currentIndex}
          handleClose={() => setLightboxOpen(false)}
          useSlideHeader={true}
          renderSlideFooter={(props) => {
            return (
              <RenderSlideFooter {...props} onTagClick={handleFilterByTag} />
            );
          }}
        />
        {/* Filters (with drawer support on mobile) */}
        <OrImageGalleryFilters
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          handleFilterChange={handleFilterChange}
        />
        {/* Results count / Reset */}
        {isPageLoading ? (
          <div
            role="progressbar"
            aria-valuetext="Loading..."
            className="mx-auto mt-[5rem] aspect-square w-[3.125rem] animate-spinner rounded-[50%] border-[0.625rem] border-t-[#ffffff] border-t-[var(--color-ui-stroke-tertiary)]"
          ></div>
        ) : hasNoResults ? (
          <div className="flex flex-col items-center justify-center gap-6 pb-20 pt-20">
            <AtHeader element="p" variant="h2">
              No Results
            </AtHeader>
            <AtClickable
              interactLocation="or-image-gallery-filters"
              type={EClickableTypes.LINK}
              variant={EClickableVariants.PRIMARY}
              size={EClickableSize.SM}
              onClick={() => {
                // Reset filters to show all
                setSelectedFilters({
                  planSeries: [],
                  room: [],
                  exterior: [],
                  location: [],
                });
              }}
              label="Reset Filters"
            />
          </div>
        ) : (
          <div className="pb-10 pt-10">
            <AtHeader element="h4">
              {filteredImages.length} Result
              {filteredImages.length !== 1 ? 's' : ''}{' '}
            </AtHeader>
          </div>
        )}

        {/* Grid */}
        <div ref={gridRef}>
          <ResponsiveMasonry columnsCountBreakPoints={{ 350: 2, 900: 3 }}>
            <Masonry gutter={gutterVal} sequential>
              {filteredImages.map(({ src, alt, width, height }, idx) => (
                <button
                  className="group relative overflow-hidden rounded-30"
                  key={`${idx}-${src}`}
                  onClick={() => handleGalleryImageClick(idx)}
                >
                  {/* Responsive Image doesn't work with our image sources??? */}
                  <ResponsiveImage
                    src={src}
                    alt={alt}
                    width={width}
                    height={height}
                    index={idx}
                    adjustedAspectRatio
                    className="h-full w-full rounded-30 object-cover transition-transform duration-300 group-hover:scale-105"
                    fill={false} // fixed size mode
                    widthPercent="45p"
                    heightPercent="45p"
                  />
                </button>
              ))}
            </Masonry>
          </ResponsiveMasonry>
        </div>
      </section>
    </>
  );
};

export interface ImageGallerySlide extends SlideImage {
  tags?: {
    PlanId?: string;
    RoomId?: string;
    ExteriorStyleId?: string;
    StudioId?: string;
  };
}

interface SlideFooterProps {
  slide: ImageGallerySlide;
  onTagClick: (key: string, value: string) => void;
}

// ------------------------------------
// renderSlideFooter
// ------------------------------------
// Footer UI for the lightbox view:
// - Shows related tags for the active slide
// - Clicking a tag applies the corresponding filter in gallery
// - Tags are sorted alphabetically
//
// Notes:
// - Uses AtTag styled buttons for tag interactions.
// - Passes filter key+value back up via onTagClick handler.

const RenderSlideFooter = ({ slide, onTagClick }: SlideFooterProps) => {
  // fetch imgs on load
  interface ILabelInfo {
    Id: number;
    Name: string;
  }

  interface IFilterLabels {
    PlanSeries: ILabelInfo[];
    Rooms: ILabelInfo[];
    ExteriorStyles: ILabelInfo[];
    Locations: ILabelInfo[];
    ImageTotals: number;
  }

  const [filterLabels, setFilterLabels] = useState<IFilterLabels>({
    PlanSeries: [],
    Rooms: [],
    ExteriorStyles: [],
    Locations: [],
    ImageTotals: 0,
  });

  useEffect(() => {
    const fetchFilterLabels = async () => {
      try {
        const res = await fetch(`/api/gallery/filters`);

        if (res.ok) {
          const data = await res.json();
          setFilterLabels(data);
        } else {
          console.error('Failed to fetch filter labels', res.statusText);
        }
      } catch (err) {
        console.error('Failed to fetch filter labels:', err);
      }
    };
    fetchFilterLabels();
  }, []);

  const tagKeyToFilterKey: Record<string, keyof IFilterLabels> = {
    PlanId: 'PlanSeries',
    RoomId: 'Rooms',
    ExteriorStyleId: 'ExteriorStyles',
    StudioId: 'Locations',
  };

  if (!slide?.tags) return <></>;

  // Extract + sort tags alphabetically
  const filterOrder = ['PlanId', 'RoomId', 'ExteriorStyleId', 'StudioId'];
  const tags = Object.entries(slide.tags)
    .filter(
      ([key, value]) =>
        value &&
        ['PlanId', 'RoomId', 'ExteriorStyleId', 'StudioId'].includes(key),
    )
    .sort(([a], [b]) => filterOrder.indexOf(a) - filterOrder.indexOf(b));

  if (tags.length === 0) return <></>;

  return (
    <div
      className="absolute bottom-0 left-0 right-0 flex min-h-[6.625rem] flex-wrap items-center justify-center border-t border-[var(--color-neutral-200)] bg-white p-4"
      style={{ fontFamily: 'figtree' }}
    >
      <div className="mx-3 font-medium text-[var(--color-neutral-400)]">
        Select related filter:
      </div>

      <div className="my-3 flex flex-wrap justify-center gap-2">
        {tags.map(([key, value]) => {
          const filterKey = tagKeyToFilterKey[key];
          const labelList = filterLabels[filterKey];
          let matchedName: string | undefined;

          if (Array.isArray(labelList)) {
            matchedName = labelList.find(
              (item) => String(item.Id) === String(value),
            )?.Name;
          }

          return (
            <AtTag
              key={key}
              variant={ETagVariants.DEFAULT}
              size={EClickableSize.SM}
              onClick={() => onTagClick(key, value)} // Apply filter on click
              className="cursor-pointer transition-colors hover:bg-[var(--color-blue-200)] focus:bg-[var(--color-blue-200)] focus:outline-[1px] focus:-outline-offset-4 focus:outline-[var(--color-blue-500)]"
            >
              {matchedName}
            </AtTag>
          );
        })}
      </div>
    </div>
  );
};
